//  CODE TO BE REPLACED WHEN FIGMA is completed for NON-TERM INDEX LOAN MERGES

import { Text } from "@mantine/core"
import { useForm } from "@mantine/form"
import BlackSelectInput from "app/views/components/inputs/BlackSelectInput"
import { useEffect } from "react"
import MergeAddNewLoan from "./merge-add-new-loan"
import MergeLoansPaydownInfoMessage from "./merge-loans-paydown-info-message"
import { ReactComponent as PlusIcon } from 'app/styles/add-circle-sharp.svg'
import { ReactComponent as ChevronDownIcon } from 'app/styles/chevronDown.svg'
import { LoanParams } from "app/models/loan-params"
import { LoanMergeParams } from "app/models/loan-merge-params"

type NewLoan = {
    indexRateOption: string
    amount: string
    borrower: string
    startDate: Date | null
    maturityDate: Date | null
    interestBaseRate: string
    interestBaseRateRounding: string
    dayBasis: string
    margin: string
    cas: string
    allInRate: string
    facilityId: string
    currency: string
    // Rate to the facility base currency; '' when they match
    fxRate: string
    isNonProRata: boolean
    rounding: string
    pikPremium: string
    pikInterestPercent: string
    cashInterestPercent: string
    newLoanWithPik: string
}

type Params = {
    selectedLoans?: LoanParams[]
    closeModal: () => void
    existingMergeData?: LoanMergeParams
}

export default function NonTermRateForm({
    selectedLoans,
    closeModal,
    existingMergeData // eslint-disable-line @typescript-eslint/no-unused-vars
}: Params) {
    const form = useForm({
        initialValues: {
            mergeType: 'single',
            newLoans: [
                {
                    indexRateOption: '',
                    amount: '',
                    borrower: '',
                    startDate: null,
                    maturityDate: null,
                    interestBaseRate: '',
                    interestBaseRateRounding: '',
                    dayBasis: '',
                    margin: '',
                    cas: '',
                    allInRate: '',
                    facilityId: '',
                    currency: '',
                    fxRate: '',
                    isNonProRata: false,
                    rounding: '',
                    pikPremium: '0',
                    pikInterestPercent: '0',
                    cashInterestPercent: '0',
                    newLoanWithPik: '',
                }
            ] as NewLoan[]
        }
    })

    // Reset form when selectedLoans changes (when user selects different loans or modal opens/closes)
    useEffect(() => {
        form.setValues({
            mergeType: 'single',
            newLoans: [{
                indexRateOption: '',
                amount: '',
                borrower: '',
                startDate: null,
                maturityDate: null,
                interestBaseRate: '',
                interestBaseRateRounding: '',
                dayBasis: '',
                margin: '',
                cas: '',
                allInRate: '',
                facilityId: '',
                currency: '',
                fxRate: '',
                isNonProRata: false,
                rounding: '',
                pikPremium: '0',
                pikInterestPercent: '0',
                cashInterestPercent: '0',
                newLoanWithPik: '',
            }]
        })
    }, [selectedLoans])

    const mergeTypeOptions = [
        { value: 'single', label: 'Create Single New Loan' },
        { value: 'multiple', label: 'Split Into Multiple New Loans' }
    ]

    // Helper function to create empty loan object
    const createEmptyLoan = (): NewLoan => ({
        indexRateOption: '',
        amount: '',
        borrower: '',
        startDate: null,
        maturityDate: null,
        interestBaseRate: '',
        interestBaseRateRounding: '',
        dayBasis: '',
        margin: '',
        cas: '',
        allInRate: '',
        facilityId: '',
        currency: '',
        fxRate: '',
        isNonProRata: false,
        rounding: '',
        pikPremium: '0',
        pikInterestPercent: '0',
        cashInterestPercent: '0',
        newLoanWithPik: '',
    })

    // Update loans array based on merge type
    const handleMergeTypeChange = (value: string) => {
        form.setFieldValue('mergeType', value)
        // Always reset loans with empty amounts when changing merge type
        if (value === 'single') {
            form.setFieldValue('newLoans', [createEmptyLoan()])
        } else if (value === 'multiple') {
            form.setFieldValue('newLoans', [createEmptyLoan(), createEmptyLoan()])
        }
        // Clear any existing validation errors
        form.clearErrors()
    }

    const handleAmountChange = (index: number, value: string) => {
        form.setFieldValue(`newLoans.${index}.amount`, value)
    }

    // Handlers for more loan options panel fields
    const handleFieldChange = (index: number, field: keyof NewLoan, value: string | Date | null) => {
        form.setFieldValue(`newLoans.${index}.${field}`, value)
    }

    const handleSubmit = (values: typeof form.values) => {
        console.log('Form submitted with values:', values)
        // TODO: Add actual submission logic
        closeModal()
    }

    const handleAddLoan = () => {
        const currentLoans = form.values.newLoans
        form.setFieldValue('newLoans', [...currentLoans, createEmptyLoan()])
    }

    const handleRemoveLoan = (index: number) => {
        const currentLoans = form.values.newLoans
        if (currentLoans.length > 1) {
            const updatedLoans = currentLoans.filter((_, i) => i !== index)
            form.setFieldValue('newLoans', updatedLoans)
        }
    }

    const renderNewLoans = () => {
        return form.values.newLoans.map((loan, index) => (
            <MergeAddNewLoan
                key={index}
                index={index}
                loan={loan}
                mergeType={form.values.mergeType}
                selectedLoans={selectedLoans}
                onIndexRateOptionChange={(idx, value) => form.setFieldValue(`newLoans.${idx}.indexRateOption`, value)}
                onAmountChange={handleAmountChange}
                onFieldChange={handleFieldChange}
                onRemove={handleRemoveLoan}
                showRemoveButton={form.values.newLoans.length > 1}
            />
        ))
    }

    return (
        <form className="merge-loans-form" onSubmit={form.onSubmit(handleSubmit)}>

            <BlackSelectInput
                label="Merge Type"
                data={mergeTypeOptions}
                value={form.values.mergeType}
                onChange={handleMergeTypeChange}
                classNames={{
                    label: "black-input-label-with-padding",
                    input: "merge-form-editable-select"
                }}
                rightSection={<ChevronDownIcon />}
            />

            {form.values.mergeType && (
                <div className="merge-loans-new-loans-container">
                    {renderNewLoans()}

                    {form.values.mergeType === 'multiple' && (
                        <div className="add-loan-action" onClick={handleAddLoan}>
                            <PlusIcon />
                            <Text className="add-loan-action-text">Add Loan</Text>
                        </div>
                    )}
                </div>
            )}

            {/* Paydown info section for multiple merge type */}
            {form.values.mergeType === 'multiple' && (

                <div className="merge-loans-paydown-section">
                    <MergeLoansPaydownInfoMessage
                        selectedLoans={selectedLoans}
                        newLoanAmounts={form.values.newLoans.map(loan => loan.amount)}
                        isMultipleMode={true}
                    />
                </div>

            )}


            <div className="actions-buttons-block">
                <button className="merge-loan-cancel-button" onClick={() => closeModal()}>Cancel</button>
                <button
                    type="submit"
                    className="merge-loan-save-button"
                >
                    Save
                </button>
            </div>

        </form>
    )
}